import { useState } from 'react';
import { Button } from '@astryxdesign/core/Button';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Bell, BellRinging, CalendarPlus } from '@phosphor-icons/react';
import { api, humanError } from '../lib/api';
import { bookingIcs, downloadIcs } from '../lib/ics';
import { pushSupport, subscribePush } from '../lib/pwa';
import type { ClientBooking } from '../lib/types';

/**
 * Reminder a day before. The state shown is the server's: "включено" only
 * after the subscription is stored and a job is planned. Where push cannot
 * work (iPhone without installation, denied, preview) we say so and offer
 * the calendar file instead.
 */
export function ReminderControls({ booking, token, onChanged }: { booking: ClientBooking; token: string; onChanged?: () => void }) {
  const [state, setState] = useState<string>(booking.reminder?.state ?? 'none');
  const [error, setError] = useState<string | null>(null);
  const support = pushSupport();
  const end = new Date(booking.work_ends_at);
  const icsEnd = booking.duration_min > 600 ? new Date(new Date(booking.starts_at).getTime() + 60 * 60000) : end;

  const enable = async () => {
    setError(null);
    try {
      const sub = await subscribePush(booking.studio.slug);
      const r = await api.savePush(token, sub);
      setState(r.state === 'scheduled' ? 'pending' : r.state);
      onChanged?.();
    } catch (e) {
      const m = (e as Error).message;
      setError(
        m === 'denied'
          ? 'Уведомления запрещены в настройках браузера. Добавьте запись в календарь.'
          : m === 'no_worker'
            ? 'Браузер не поддерживает уведомления. Добавьте запись в календарь.'
            : humanError(e),
      );
    }
  };

  let status: string | null = null;
  if (state === 'pending') status = 'Напомним за сутки до визита.';
  else if (state === 'sent') status = 'Напоминание отправлено.';
  else if (state === 'preview') status = 'Это образец: уведомления в нём не отправляются.';
  else if (state === 'too_late') status = 'До визита меньше двух часов — напоминание уже не нужно.';

  return (
    <VStack gap={3}>
      {status ? (
        <HStack gap={2} vAlign="center">
          <BellRinging size={20} color="var(--color-accent)" aria-hidden />
          <Text>{status}</Text>
        </HStack>
      ) : support.ok ? (
        <Button label="Напомнить за сутки" variant="secondary" icon={<Bell size={18} aria-hidden />} clickAction={enable} width="100%" />
      ) : (
        <Text type="supporting">
          {support.reason === 'ios_needs_install'
            ? 'На iPhone уведомления работают только в установленном приложении: «Поделиться» → «На экран „Домой“». Пока можно добавить запись в календарь.'
            : support.reason === 'denied'
              ? 'Уведомления запрещены в браузере — добавьте запись в календарь.'
              : 'Уведомления здесь недоступны — добавьте запись в календарь, он напомнит сам.'}
        </Text>
      )}
      {error && (
        <Text type="supporting" color="secondary">
          {error}
        </Text>
      )}
      <Button
        label="Добавить в календарь"
        variant="secondary"
        width="100%"
        icon={<CalendarPlus size={18} aria-hidden />}
        onClick={() =>
          downloadIcs(
            `zapis-${booking.studio.slug}.ics`,
            bookingIcs({
              id: booking.id,
              startsAt: booking.starts_at,
              endsAt: icsEnd.toISOString(),
              title: `${booking.service_name} — ${booking.studio.name}`,
              location: booking.studio.address,
              description: `Телефон студии: ${booking.studio.phone ?? ''}`,
              url: `${location.origin}/s/${booking.studio.slug}/my`,
            }),
          )
        }
      />
    </VStack>
  );
}
